import React from 'react'

import { GptAdProviderContext } from '../GptAdProvider/GptAdProviderContext'
import styled from '@emotion/styled'
import { withClass } from 'common/emotion'
import { usePrevious } from 'common/hooks/usePrevious'
import { supportsMutationObserver } from 'common/helpers/supportsMutationObserver'

export const gptAdSlotClassname = 'adSlotClassName'

export const StyledGptAd = withClass(gptAdSlotClassname)(styled('div')())

export type SlotMutationEvent = MutationEvent

export interface Props {
    id: string
    disabled?: boolean
    adContainerRef: React.MutableRefObject<HTMLDivElement | null>
    onSlotRenderEnded?: (e: googletag.events.SlotRenderEndedEvent) => void
    onSlotMutation?: (mutations: MutationRecord[]) => void
}

export const GptAdSlot: React.FC<Props> = props => {
    useRegisterSlot(props)
    useHandleAdMutation(props.id, props.onSlotMutation)
    useTriggerAdsOnEnable(props.disabled)

    return <StyledGptAd id={props.id} />
}

/**
 * Manage the registration of the ad slot with the GptAdProvider
 **/
function useRegisterSlot(props: Props) {
    const { id } = props
    const disabled = Boolean(props.disabled)
    const gptAdProvider = React.useContext(GptAdProviderContext)

    // On slot render ended is NOT tracked in the deps - this because it's pretty easy to set from a component way
    // up the chain as an inline function and thus cause infinite re-renders. If you need to set a new callback for this,
    // then you need to set a new key on the component so it does a full re-render
    // TODO: investigate if we can change the above so "onSlotRenderEnded" IS part of the dependencies
    const onSlotRenderEnded = React.useCallback((event: googletag.events.SlotRenderEndedEvent) => {
        if (props.onSlotRenderEnded) props.onSlotRenderEnded(event)
    }, [])

    // We need to use the adContainer rather than the slot ref because when
    // the slot is hidden until loaded getBoundingClientRect will always return
    // 0, meaning the ad is treated in the viewport. The container is never hidden
    // so it's safe to use
    const getViewportPosition = React.useCallback(
        () => getAdRefViewportPosition(props.adContainerRef.current),
        [props.adContainerRef]
    )

    const slotDefinition = React.useMemo(
        () => ({
            id,
            disabled,
            onSlotRenderEnded,
            getViewportPosition,
        }),
        [id, disabled, onSlotRenderEnded, getViewportPosition]
    )

    React.useEffect(() => {
        if (!gptAdProvider) throw new Error("GptAdProvider must be rendered above GptAdSlot's")
        gptAdProvider.registerSlot(slotDefinition)
        return () => gptAdProvider.unregisterSlot(slotDefinition)
    }, [gptAdProvider, slotDefinition])
}

/**
 * Tell the gptAdProvider to trigger an ad load if an ad goes form disabled to enabled
 **/
function useTriggerAdsOnEnable(disabled?: boolean) {
    const gptAdProvider = React.useContext(GptAdProviderContext)
    const prevDisabled = usePrevious(disabled)
    React.useEffect(() => {
        if (gptAdProvider && prevDisabled && !disabled) gptAdProvider.loadDebounced()
    }, [prevDisabled, disabled])
}

/**
 * Sets up a mutation handler for Ad unit elements. Will use a mutation observer to track these changes and call
 * the onSlotMutation callback.
 **/
function useHandleAdMutation(adId: string, onSlotMutation?: (mutations: MutationRecord[]) => void) {
    const handleMutation = React.useCallback(
        (mutations: MutationRecord[]) => {
            if (onSlotMutation) onSlotMutation(mutations)
        },
        [onSlotMutation]
    )

    React.useEffect(() => {
        const ad = document.getElementById(adId)!
        const cleanupObserver = observeAdSlotMutations(ad, handleMutation)
        return cleanupObserver
    }, [handleMutation])
}

function observeAdSlotMutations(
    ad: HTMLElement,
    handleMutation: (mutations: MutationRecord[]) => void
) {
    if (supportsMutationObserver()) {
        const observer = new MutationObserver(handleMutation)
        observer.observe(ad, { attributes: true, childList: true })
        return () => observer.disconnect()
    }

    // return noop if ther is no mutation observer in the window context
    return () => {}
}

function getAdRefViewportPosition(ref: HTMLDivElement | null): undefined | number {
    if (typeof window === 'undefined') {
        return undefined
    }

    if (!ref) {
        return undefined
    }

    const pos = ref.getBoundingClientRect()
    const viewportHeight = window.innerHeight

    if (process.env.NODE_ENV === 'test') {
        return 0
    }

    // in viewport
    if (pos.top <= viewportHeight && pos.bottom >= 0) {
        return 0
    }

    // above viewport
    if (pos.bottom < 0) {
        return pos.bottom
    }

    // below viewport
    return pos.top - viewportHeight
}
